import dayjs from "dayjs";
import { translations } from "./lang";
import { APP_COLORS } from "./helpers";

export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1); // 1–31
const COLORS = [
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
  "#24ebac",
  "#ebac24",
  "#24c6eb",
];
const maps = (lang) => ({
  app: translations[lang].filter.apps,
});

function normalizeKey(val) {
  return String(val).trim().toLowerCase();
}

function aggregateByGroup(data, groupKey, mode = "amount") {
  const agg = {};

  data.forEach((item) => {
    // normalize the group key
    const rawGroup = item[groupKey];
    const group = normalizeKey(rawGroup);

    const dayNum = dayjs(item.date).date();

    if (!agg[group]) {
      agg[group] = {};
      // keep the first seen original label for display later
      agg[group]._label = rawGroup;
    }

    if (!agg[group][dayNum]) agg[group][dayNum] = 0;

    agg[group][dayNum] +=
      mode === "amount" ? item.completedAmount || 0 : item.completed || 0;
  });

  return agg;
}

export function prepareLineSeries(
  data,
  groupKey = "app",
  mode = "amount",
  topN = 6,
  lang = "ar"
) {
  // 1️⃣ Aggregate
  const agg = aggregateByGroup(data, groupKey, mode);

  // 2️⃣ If topN is given, sort and trim
  let groups = Object.keys(agg);
  if (topN) {
    const totals = groups.map((g) => ({
      group: g,
      total: Object.values(agg[g]).reduce((a, b) => a + b, 0),
    }));
    groups = totals
      .sort((a, b) => b.total - a.total)
      .slice(0, topN)
      .map((t) => t.group);
  }

  // 3️⃣ Build series
  return groups.map((group, i) => ({
    label:
      groupKey === "app"
        ? maps(lang)[groupKey]?.[group] ??
          translations[lang].notApp + " " + group[group.length - 1]
        : group,
    data: DAYS.map((d) => agg[group]?.[d] || 0),
    color: getAppColor(group, i),
    showMark: false,
  }));
}

export function findMissingSalesDaysWithGaps(dailyData, daysBack = 14) {
  // 1. Group apps & recorded dates per store-branch
  const storeBranchInfo = {};

  dailyData.forEach(({ storeName: store, branchName: branch, app, date }) => {
    if (!storeBranchInfo[store]) storeBranchInfo[store] = {};
    if (!storeBranchInfo[store][branch]) {
      storeBranchInfo[store][branch] = {
        apps: new Set(),
        dates: new Set(),
      };
    }
    storeBranchInfo[store][branch].apps.add(app);
    storeBranchInfo[store][branch].dates.add(date);
  });

  // 2. Build lookup map: store|branch|app|date -> completed (Number)
  const salesMap = new Map();
  dailyData.forEach(({ storeName: store, branchName: branch, app, date, completed }) => {
    salesMap.set(`${store}|${branch}|${app}|${date}`, Number(completed || 0));
  });

  // 3. Walk day-by-day and apply rule:
  //    - If a day has real sales, update lastSaleDate/lastAmount
  //    - If missing AND last sale was exactly previous date -> decline = lastAmount
  //    - Otherwise decline = 0
  const missingDaysRows = [];
  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setDate(today.getDate() - daysBack);

  for (const store in storeBranchInfo) {
    for (const branch in storeBranchInfo[store]) {
      const { apps, dates } = storeBranchInfo[store][branch];

      // parse & sort recorded dates
      const sortedDates = Array.from(dates)
        .map((d) => {
          const [day, mon, year] = d.split(/[-/]/);
          return {
            raw: d,
            dateObj: new Date(`${mon} ${day}, ${year}`),
          };
        })
        .sort((a, b) => a.dateObj - b.dateObj);

      // keep only recent dates and drop very last (in-progress) date
      const recentDates = sortedDates.filter((d) => d.dateObj >= cutoff);
      const limitedDates = recentDates.slice(0, -1);
      if (limitedDates.length === 0) continue;

      apps.forEach((app) => {
        let lastSaleDate = null; // Date object of last actual sale day
        let lastAmount = null; // numeric completed value of last sale

        const start = new Date(limitedDates[0].dateObj);
        const end = new Date(limitedDates[limitedDates.length - 1].dateObj);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = d.getDate();
          const mon = d.toLocaleString("en-US", { month: "long" });
          const raw = `${day}-${mon}-${d.getFullYear()}`;
          const key = `${store}|${branch}|${app}|${raw}`;

          if (salesMap.has(key)) {
            // actual recorded sales on this day -> update lastSaleDate/lastAmount
            lastSaleDate = new Date(d); // copy
            lastAmount = salesMap.get(key);
          } else {
            // missing day -> only carry forward if lastSaleDate was exactly previous day
            let decline;
            if (lastSaleDate) {
              const diffDays = Math.round((d - lastSaleDate) / 86400000);
              decline = diffDays === 1 ? lastAmount : 0;
            } else {
              decline = 0;
            }

            missingDaysRows.push({
              store,
              branch,
              app,
              date: raw,
              decline,
            });
          }
        }
      });
    }
  }

  return missingDaysRows;
}

const DEFAULT_COLORS = [
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
  "#24ebac",
  "#ebac24",
  "#24c6eb",
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
];
const getAppColor = (app, index) =>
  APP_COLORS[app] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

export const preparePieCountByAppAndBranch = (
  data,
  lang = "ar",
  topNBranch = 6
) => {
  const aggApp = {};
  const aggBranch = {};
  const originalLabelsBranch = {};

  data.forEach((item) => {
    const orderAmount = Number(item.orderAmount) || 0;
    const enduranceAmount = Number(item.enduranceAmount) || 0;

    // ---- APP aggregation ----
    const appKey = item.app ?? "Unknown";
    if (!aggApp[appKey]) {
      aggApp[appKey] = { count: 0, orderAmount: 0, enduranceAmount:0 };
    }
    aggApp[appKey].count += 1;
    aggApp[appKey].orderAmount += orderAmount;
    aggApp[appKey].enduranceAmount += enduranceAmount;

    // ---- BRANCH aggregation (normalized) ----
    const rawBranch = item.branchName ?? "Unknown";
    const branchKey = String(rawBranch).trim().toLowerCase();
    if (!aggBranch[branchKey]) {
      aggBranch[branchKey] = { count: 0, orderAmount: 0, enduranceAmount };
      originalLabelsBranch[branchKey] = rawBranch;
    }
    aggBranch[branchKey].count += 1;
    aggBranch[branchKey].orderAmount += orderAmount;
    aggBranch[branchKey].enduranceAmount += enduranceAmount;
  });

  const totalApp = Object.values(aggApp).reduce((sum, v) => sum + v.count, 0);

  const totalBranch = Object.values(aggBranch).reduce(
    (sum, v) => sum + v.count,
    0
  );

  // ---- APP pie data ----
  const pieDataApp = {
    completed: Object.entries(aggApp).map(([label, v], idx) => ({
      label:
        maps(lang).app?.[label] ??
        translations[lang].notApp + " " + label[label.length - 1],
      value: v.count,
      color: getAppColor(label, idx),
    })),

    completedAmount: Object.entries(aggApp).map(([label, v], idx) => ({
      label:
        maps(lang).app?.[label] ??
        translations[lang].notApp + " " + label[label.length - 1],
      value: v.orderAmount.toFixed(2),
      color: getAppColor(label, idx),
    })),
  };

  // ---- BRANCH pie data (top N) ----
  const pieDataBranch = {
    completed: Object.entries(aggBranch)
      .map(([normKey, v], idx) => ({
        label: originalLabelsBranch[normKey],
        value: v.count,
        color: COLORS[idx % COLORS.length],
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topNBranch),

    completedAmount: Object.entries(aggBranch)
      .map(([normKey, v], idx) => ({
        label: originalLabelsBranch[normKey],
        value: v.orderAmount.toFixed(2),
        color: COLORS[idx % COLORS.length],
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topNBranch),
  };
  const amountCountAvgByApp = Object.entries(aggApp).map(([label, v]) => {
    const count = v.count;
    const amount = v.orderAmount;
    const enduranceAmount = v.enduranceAmount;

    return {
      label:
        maps(lang).app?.[label] ??
        translations[lang].notApp + " " + label[label.length - 1],
      value: amount, // Amount
      value2: count, // Count
      value3: enduranceAmount, // enduranceAmount
    };
  });
  const amountCountAvgByBranch = Object.entries(aggBranch)
  .map(([normKey, v]) => {
    const count = v.count;
    const amount = v.orderAmount;
    const enduranceAmount = v.enduranceAmount;

    return {
      label: originalLabelsBranch[normKey],
      value: amount,  // Amount
      value2: count,  // Count
      value3: enduranceAmount,    // enduranceAmount
    };
  })
  // 🔽 sort by count (DESC)
  .sort((a, b) => b.value2 - a.value2)
  // 🔽 limit to top N branches
  .slice(0, topNBranch);

  return {
    pieDataApp,
    pieDataBranch,
    totalApp,
    totalBranch,
    amountCountAvgByApp,
    amountCountAvgByBranch,
  };
};
